import type { WorkbookVersion } from '@bloo-team/api-client'
import { sameVersion, versionLabel } from '../../lib/workbook-blocks'

/**
 * The History tab of the workbook page (#205, design B · Tab): every version
 * from `workbook/versionlist`, newest first, no paging. The latest is marked
 * *current*; each older one can be viewed (the page's `?version=`) or
 * reverted to (the page confirms). Compare is not built.
 */

export interface HistoryTabProps {
  /** Newest first. */
  versions: WorkbookVersion[]
  isPending: boolean
  isError: boolean
  /** The version on screen when it is an old one (`?version=`), else null. */
  viewing: string | null
  canRevert: boolean
  /** Why Revert is off, when it is. */
  revertReason: string | null
  onView: (version: string) => void
  onRevert: (version: string) => void
}

const when = (s: number | null) => (s ? `${new Date(s * 1000).toISOString().slice(0, 16).replace('T', ' ')} UTC` : '—')

export function HistoryTab({ versions, isPending, isError, viewing, canRevert, revertReason, onView, onRevert }: HistoryTabProps) {
  if (isPending) return <p className="hud-unit" role="status">Loading versions…</p>
  if (isError) {
    return (
      <div className="bui-alert bui-alert-error" role="alert">
        <span className="bui-alert-mark">✕</span>
        <span>The version history could not be loaded.</span>
      </div>
    )
  }
  if (versions.length === 0) return <p className="hud-unit" role="status">No versions recorded.</p>
  return (
    <div className="dense-table-wrap">
      <table className="dense-table" aria-label="Versions">
        <thead>
          <tr>
            <th>version</th>
            <th>commit message</th>
            <th>author</th>
            <th>date</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {versions.map((v, i) => {
            const latest = i === 0
            const label = versionLabel(v.version)
            const shown = viewing !== null && sameVersion(viewing, v.version)
            return (
              <tr key={v.version} data-open={shown || undefined}>
                <td className="mono">
                  {label}
                  {latest ? <span className="bui-chip bui-chip-accent wb-chip-gap">current</span> : null}
                  {shown ? <span className="bui-chip bui-chip-warn wb-chip-gap">viewing</span> : null}
                </td>
                <td>{v.commit_msg || '—'}</td>
                <td className="muted">{v.version_author || '—'}</td>
                <td className="mono muted">{when(v.last_modified_tstamp)}</td>
                <td>
                  {latest ? null : (
                    <span className="hud-row wb-gap-1">
                      <button
                        type="button"
                        className="bui-btn bui-btn-ghost bui-btn-sm"
                        aria-label={`View ${label}`}
                        disabled={shown}
                        onClick={() => onView(v.version)}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="bui-btn bui-btn-ghost bui-btn-sm"
                        aria-label={`Revert to ${label}`}
                        disabled={!canRevert}
                        title={canRevert ? undefined : (revertReason ?? undefined)}
                        onClick={() => onRevert(v.version)}
                      >
                        Revert…
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
